import { HttpClient, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Component, Injectable, Injector, OnDestroy, inject, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { environment as env } from '@env';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { firstValueFrom, from, switchMap, throwError } from 'rxjs';

/** PRD 9.3 prefill as stored on the session (backend ActionSessionsService.prefill). */
export interface MailPrefill {
    customer_id: number | null;
    contact_id: number | null;
    contact_name: string | null;
    contact_email: string | null;
    enquiry_id: number | null;
    subject: string | null;
    notes: string | null;
    assignee_user_id: number | null;
    /** YYYY-MM-DD, in-app timezone */
    date: string | null;
    web_link: string | null;
}

interface ActionSession {
    token: string;
    work_item_id: number;
    action_type: string;
    target_record_id: number | null;
    status: 'open' | 'expired';
    prefill: MailPrefill;
}

const BASE = env.apiUrl.replace(/\/+$/, '') + '/mail';
/** Save endpoints with the BE-8 hook; anything else never gets the token. */
const SAVE_URL = /\/(enquiry\/(create(\/duplicate\/customer\/rfq\/vessel)?|update)|quotation\/(create|tender\/update))$/;

/** The session of the page on screen; read by the interceptor, which lives outside the page injector. */
let active: MailSession | null = null;

/**
 * `?mail_session=TOKEN` on an ERP page (PRD 9.2). Provide it on the page component; the page awaits `prefill`
 * (null without a usable session) and places `<app-mail-session-banner />`. Leaving the page (route change, tab
 * close) abandons the session; the server ignores that once the session is saved.
 */
@Injectable()
export class MailSession implements OnDestroy {
    // Resolved on first use: pages opened without a token make no mail calls (and their specs provide no HttpClient).
    private _injector = inject(Injector);
    private get _http() {
        return this._injector.get(HttpClient);
    }

    readonly token = inject(ActivatedRoute).snapshot?.queryParamMap?.get('mail_session') ?? null;
    readonly session = signal<ActionSession | null>(null);
    readonly error = signal<string | null>(null);
    private _confirmed = false;
    private readonly _leave = () => this._abandon();
    readonly prefill: Promise<MailPrefill | null> = this.token ? this._load(this.token) : Promise.resolve(null);

    private async _load(token: string): Promise<MailPrefill | null> {
        try {
            const r = await firstValueFrom(
                this._http.get<{ data: ActionSession }>(`${BASE}/action-sessions/${encodeURIComponent(token)}`)
            );
            this.session.set(r.data);
            // eslint-disable-next-line @typescript-eslint/no-this-alias -- read by the interceptor (see `active`)
            active = this;
            window.addEventListener('pagehide', this._leave);
            return r.data.prefill;
        } catch (e) {
            this.error.set((e as HttpErrorResponse)?.error?.message || 'This email action could not be loaded.');
            return null;
        }
    }

    /**
     * PRD 9.6: warn once if the item was addressed meanwhile; the save still goes through either way.
     * ponytail: re-opening the same action is today's only item-status read (returns this open session, or 409
     * once the item is closed); expired sessions skip it. Swap for an item status on GET /action-sessions/:token.
     */
    async confirmSave(): Promise<boolean> {
        const s = this.session();
        if (this._confirmed || s?.status !== 'open') return true;
        const err = await firstValueFrom(
            this._http.post(`${BASE}/work-items/${s.work_item_id}/actions`, {
                actionType: s.action_type,
                targetRecordId: s.target_record_id ?? undefined,
            })
        ).then(
            () => null,
            (e: HttpErrorResponse) => e
        );
        if (err?.status !== 409) return true;
        const r = await firstValueFrom(
            this._injector
                .get(FuseConfirmationService)
                .open({
                    title: 'Email already handled',
                    message: `${err.error?.message ?? 'This email is already handled'}. Saving still saves the record, but the email stays as it is.`,
                    actions: { confirm: { label: 'Save anyway', color: 'primary' }, cancel: { label: 'Cancel' } },
                })
                .afterClosed()
        );
        return (this._confirmed = r === 'confirmed');
    }

    ngOnDestroy(): void {
        this._abandon();
        if (active === this) active = null;
    }

    /** sendBeacon can't carry the auth / API key headers, so a keepalive fetch (token as AuthService stores it). */
    private _abandon(): void {
        if (!this.session()) return;
        this.session.set(null);
        window.removeEventListener('pagehide', this._leave);
        fetch(`${BASE}/action-sessions/${encodeURIComponent(this.token!)}/abandon`, {
            method: 'POST',
            keepalive: true,
            headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'X-API-Key': env.apiKey },
        }).catch(() => undefined);
    }
}

/** Adds `mail_session` to the page's save body (BE-8 hooks) while a session page is on screen. */
export const mailSessionInterceptor: HttpInterceptorFn = (req, next) => {
    const s = active;
    const body = req.body;
    if (!s || !SAVE_URL.test(req.url.split('?')[0]) || !body || typeof body !== 'object' || body instanceof FormData) {
        return next(req);
    }
    return from(s.confirmSave()).pipe(
        switchMap((ok) =>
            ok
                ? next(req.clone({ body: { ...body, mail_session: s.token } }))
                : throwError(() => new HttpErrorResponse({ status: 409, url: req.url, error: { message: 'Save cancelled; nothing was saved.' } }))
        )
    );
};

/** "Pre-filled from email" strip (Control Tower colours) for pages providing MailSession. */
@Component({
    selector: 'app-mail-session-banner',
    standalone: true,
    imports: [MatIconModule],
    template: `
        @if (ms.session(); as s) {
            <div
                class="mx-4 mt-3 flex items-center gap-3 rounded-xl bg-[linear-gradient(135deg,#0f172a_0%,#1e3a5f_48%,#0f2744_100%)] px-4 py-2.5 text-white shadow-md"
                data-testid="mail-session-banner">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 ring-1 ring-white/15">
                    <mat-icon class="text-sky-300 icon-size-5">mail</mat-icon>
                </span>
                <div class="min-w-0 flex-auto">
                    <div class="text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-300">Pre-filled from email</div>
                    <div class="truncate font-medium" [title]="s.prefill.subject ?? ''">{{ s.prefill.subject || '(no subject)' }}</div>
                </div>
                @if (s.status === 'expired') {
                    <span class="hidden rounded-full bg-amber-400/15 px-2 py-0.5 text-xs text-amber-200 sm:inline">Timed out, saving still counts</span>
                }
                @if (s.prefill.web_link?.startsWith('https://')) {
                    <a
                        class="shrink-0 rounded-lg border border-white/20 px-3 py-1.5 text-sm hover:bg-white/10"
                        [href]="s.prefill.web_link"
                        target="_blank"
                        rel="noopener noreferrer">Open in Outlook</a>
                }
            </div>
        } @else if (ms.error()) {
            <div class="mx-4 mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700" data-testid="mail-session-error">
                Email action not available ({{ ms.error() }}). The page works as usual; start the action again from the mail dashboard.
            </div>
        }
    `,
})
export class MailSessionBannerComponent {
    readonly ms = inject(MailSession);
}
